import { MotionValue } from "motion/react"
import { MutableRefObject, useLayoutEffect, useMemo, useRef } from "react"

/**
 * Similar to `useMeasure()` from `react-use`, but without triggering a React re-render.
 *
 * The {width, height} values are MotionValue objects, which can be subscribed to or piped to other motion utils.
 */
export function useMotionSize<E extends Element = HTMLDivElement>(): MotionSizeTuple<E> {
  const ref = useRef<E | null>(null)
  const bounds: MotionSize = useMemo(() => {
    return { ready: false, width: new MotionValue(0), height: new MotionValue(0) }
  }, [])

  useLayoutEffect(() => {
    if (!ref.current) {
      bounds.ready = false
      return
    }
    const ro = new ResizeObserver((entries) => {
      const size = entries[0].borderBoxSize[0]
      ;(bounds as MotionSize).ready = true
      bounds.width.set(size.inlineSize)
      bounds.height.set(size.blockSize)
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  })

  return [ref, bounds]
}

type MotionSize = {
  ready: boolean
  width: MotionValue<number>
  height: MotionValue<number>
}

type MotionSizeTuple<E> = [ref: MutableRefObject<E | null>, bounds: MotionSize]
